/**
 * Поля формы модели по схеме паспорта: текст, число, таблица по зонам и
 * годам, список по видам масел. Значения — строками в состоянии формы;
 * проверку и сборку тела делает `buildModelPayload` из `@entities/fleet`.
 */
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Input } from '@shared/ui/Input';
import {
  Control,
  ControlArea,
  FormGrid,
  FormRow,
  Matrix,
  MatrixBox,
  PrcMark,
  ScrollRegion,
  fleetType,
  type PassportFormItem,
} from '@entities/fleet';
import { FieldError, FieldHint, FieldLabel, TextButton, invalidControl } from './ui';

// Сетка и поля формы — общие для экранов техники (`@entities/fleet`).
export { Control, ControlArea, FormGrid, FormRow };

/** Доступ полей к состоянию формы — один объект вместо десятка пропсов. */
export interface FieldBinding {
  value: (key: string) => string;
  onChange: (key: string, value: string) => void;
  onBlur: (key: string) => void;
  /** Ошибка, которую уже пора показать (после выхода из поля или «Сохранить»). */
  error: (key: string) => string | null;
  /** Значение коллеги, если он изменил то же поле, пока шла правка. */
  conflict: (key: string) => string | null;
  takeConflict: (key: string) => void;
  domId: (key: string) => string;
}

export type SuggestList = 'owner' | 'position' | 'fuel';

const CellInput = styled(Input)`
  min-width: 84px;
  height: 32px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  font-size: ${fleetType.body};
  text-align: right;
  font-variant-numeric: tabular-nums;
  ${invalidControl}
`;

const CellTable = styled(Matrix)`
  td {
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1)}`};
  }
`;

const ConflictNote = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border-left: 2px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.status.warning}14;
  font-size: ${fleetType.small};
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PairsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(5)}`};

  @container drawer (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PairsTitle = styled.p`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin: ${({ theme }) => `0 0 ${theme.spacing(2)}`};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function describedBy(binding: FieldBinding, key: string, hint: boolean): string | undefined {
  const ids = [
    hint ? `${binding.domId(key)}-hint` : null,
    binding.error(key) ? `${binding.domId(key)}-error` : null,
    binding.conflict(key) !== null ? `${binding.domId(key)}-conflict` : null,
  ].filter((s): s is string => s !== null);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

function Conflict({ binding, k }: { binding: FieldBinding; k: string }) {
  const fresh = binding.conflict(k);
  if (fresh === null) return null;
  return (
    <ConflictNote id={`${binding.domId(k)}-conflict`}>
      Пока вы правили, в справочнике стало: <strong>{fresh === '' ? 'пусто' : fresh}</strong>.
      Сохранится ваше значение.{' '}
      <TextButton type="button" onClick={() => binding.takeConflict(k)}>
        Взять значение из справочника
      </TextButton>
    </ConflictNote>
  );
}

/** Подпись, поле, подсказка, ошибка, конфликт — для любого одиночного поля. */
export function FieldShell({
  binding,
  k,
  label,
  prc,
  required,
  hint,
  warning,
  wide,
  children,
}: {
  binding: FieldBinding;
  k: string;
  label: string;
  prc?: boolean;
  required?: boolean;
  hint?: ReactNode;
  /** Предупреждение, которое не мешает сохранить (решает сервер). */
  warning?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  const error = binding.error(k);
  return (
    <FormRow $wide={wide}>
      <FieldLabel htmlFor={binding.domId(k)}>
        <span>
          {label}
          {required && (
            <span className="req" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </span>
        {prc && <PrcMark title="Поле участвует в расчёте цены машино-часа">ПРЦ</PrcMark>}
      </FieldLabel>
      {children}
      {hint && <FieldHint id={`${binding.domId(k)}-hint`}>{hint}</FieldHint>}
      {warning && <FieldError id={`${binding.domId(k)}-warning`}>{warning}</FieldError>}
      {error && <FieldError id={`${binding.domId(k)}-error`}>{error}</FieldError>}
      <Conflict binding={binding} k={k} />
    </FormRow>
  );
}

/** Текстовое или числовое поле паспорта. */
export function ScalarInput({
  item,
  binding,
  suggestId,
}: {
  item: Extract<PassportFormItem, { kind: 'field' }>;
  binding: FieldBinding;
  suggestId?: (list: SuggestList) => string;
}) {
  const { key, field, label } = item;
  const numeric = field.type === 'number';
  const multiline = field.type === 'text' && field.multiline === true;
  const list =
    field.type === 'text' && field.suggest && suggestId ? suggestId(field.suggest) : undefined;
  const common = {
    id: binding.domId(key),
    value: binding.value(key),
    'aria-invalid': binding.error(key) ? true : undefined,
    'aria-describedby': describedBy(binding, key, false),
    onBlur: () => binding.onBlur(key),
  };
  return (
    <FieldShell binding={binding} k={key} label={label} prc={field.prc} wide={multiline}>
      {multiline ? (
        <ControlArea {...common} rows={2} onChange={(e) => binding.onChange(key, e.target.value)} />
      ) : (
        <Control
          {...common}
          type="text"
          inputMode={numeric ? 'decimal' : undefined}
          autoComplete="off"
          spellCheck={numeric ? false : undefined}
          list={list}
          onChange={(e) => binding.onChange(key, e.target.value)}
        />
      )}
    </FieldShell>
  );
}

/** Ошибки ячеек таблицы — одной строкой под таблицей. */
function cellErrors(
  item: Extract<PassportFormItem, { kind: 'matrix' | 'pairs' }>,
  binding: FieldBinding,
): { key: string; label: string; message: string }[] {
  const cells =
    item.kind === 'pairs'
      ? item.cells
      : item.rows.flatMap((r) => r.cells.filter((c) => c !== null));
  return cells.flatMap((c) => {
    const message = binding.error(c.key);
    return message ? [{ key: c.key, label: c.label, message }] : [];
  });
}

/** Таблица по зонам или годам: поле ввода в каждой ячейке. */
export function MatrixInput({
  item,
  binding,
}: {
  item: Extract<PassportFormItem, { kind: 'matrix' }>;
  binding: FieldBinding;
}) {
  const errors = cellErrors(item, binding);
  const errorId = `${binding.domId(item.id)}-errors`;
  const conflicts = item.rows
    .flatMap((r) => r.cells)
    .filter((c): c is NonNullable<typeof c> => c !== null && binding.conflict(c.key) !== null);
  const prc = item.rows.some((r) => r.prc);
  return (
    <FormRow $wide>
      <MatrixBox>
        <ScrollRegion label={item.caption}>
          <CellTable>
            <caption>
              {item.caption}
              {prc && (
                <>
                  {' '}
                  <PrcMark title="Поле участвует в расчёте цены машино-часа">ПРЦ</PrcMark>
                </>
              )}
            </caption>
            <thead>
              <tr>
                <th scope="col">{item.corner}</th>
                {item.columns.map((c) => (
                  <th key={c.key} scope="col">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {item.rows.map((r) => (
                <tr key={r.label}>
                  <th scope="row">{r.label}</th>
                  {r.cells.map((c, i) => (
                    <td key={item.columns[i]?.key ?? i}>
                      {c && (
                        <CellInput
                          id={binding.domId(c.key)}
                          value={binding.value(c.key)}
                          aria-label={c.label}
                          aria-invalid={binding.error(c.key) ? true : undefined}
                          aria-describedby={binding.error(c.key) ? errorId : undefined}
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          spellCheck={false}
                          onChange={(e) => binding.onChange(c.key, e.target.value)}
                          onBlur={() => binding.onBlur(c.key)}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </CellTable>
        </ScrollRegion>
        {errors.length > 0 && (
          <FieldError id={errorId}>
            {errors[0]?.message}: {errors.map((e) => e.label).join('; ')}
          </FieldError>
        )}
        {conflicts.map((c) => (
          <Conflict key={c.key} binding={binding} k={c.key} />
        ))}
      </MatrixBox>
    </FormRow>
  );
}

/** Нормы по видам масел — подписанные поля парами. */
export function PairsInput({
  item,
  binding,
}: {
  item: Extract<PassportFormItem, { kind: 'pairs' }>;
  binding: FieldBinding;
}) {
  return (
    <FormRow $wide>
      <PairsTitle>
        {item.label}
        {item.prc && <PrcMark title="Поле участвует в расчёте цены машино-часа">ПРЦ</PrcMark>}
      </PairsTitle>
      <PairsGrid>
        {item.cells.map((c) => (
          <FieldShell key={c.key} binding={binding} k={c.key} label={c.label}>
            <Control
              id={binding.domId(c.key)}
              value={binding.value(c.key)}
              aria-invalid={binding.error(c.key) ? true : undefined}
              aria-describedby={describedBy(binding, c.key, false)}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => binding.onChange(c.key, e.target.value)}
              onBlur={() => binding.onBlur(c.key)}
            />
          </FieldShell>
        ))}
      </PairsGrid>
    </FormRow>
  );
}
